import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';
import * as ErrorActions from '../actions/error.actions';
import * as NotificationActions from '../../notification/actions/notification.actions';
import { Notification } from '../../notification/actions/notification.actions';
import { environment } from '../../../environments/environment';
import { ErrorMessageService } from '../../../service/error-message.service';

@Injectable()
export class ErrorEffects {
  private actions$ = inject(Actions);
  private errorMessageService = inject(ErrorMessageService);

  /**
   * Convert HttpError actions to blocking notifications
   */
  httpErrorToNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ErrorActions.HttpError),
      map(({ httpError, actionType }) => {
        const statusCode = httpError.status;

        // Get user-friendly error message from service
        const errorMessage = this.errorMessageService.getErrorMessage({
          statusCode,
          actionType,
          serverMessage: httpError.error?.message || httpError.message,
        });

        const severity = this.errorMessageService.getSeverity(statusCode);
        const notification: Notification = {
          id: Date.now().toString(),
          severity,
          title: errorMessage.title,
          summary: errorMessage.summary,
          detail: errorMessage.detail,
          timestamp: new Date(),
          duration: this.getDuration(severity),
          blocking: true, // Make it modal-like
          icon: this.errorMessageService.getErrorIcon(statusCode),
          dismissible: true,
          actions: this.errorMessageService.canRetry(statusCode)
            ? [
                {
                  label: 'Retry',
                  actionType: '[Error] Retry Action',
                  payload: { originalAction: actionType },
                },
              ]
            : undefined,
        };

        return NotificationActions.ShowNotification({ notification });
      }),
    ),
  );

  /**
   * Convert GlobalError actions to blocking notifications
   */
  globalErrorToNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ErrorActions.GlobalError),
      map(({ error }) => {
        // Get user-friendly error message from service
        const errorMessage = this.errorMessageService.getErrorMessage({
          statusCode: error.statusCode,
          actionType: error.actionType,
          serverMessage: error.message,
        });

        const severity = this.errorMessageService.getSeverity(error.statusCode);
        const notification: Notification = {
          id: Date.now().toString(),
          severity,
          title: errorMessage.title,
          summary: errorMessage.summary,
          detail: errorMessage.detail,
          timestamp: new Date(),
          duration: this.getDuration(severity),
          blocking: true, // Make it modal-like
          icon: this.errorMessageService.getErrorIcon(error.statusCode),
          dismissible: true,
          actions: this.errorMessageService.canRetry(error.statusCode)
            ? [
                {
                  label: 'Retry',
                  actionType: '[Error] Retry Action',
                  payload: { originalAction: error.actionType },
                },
              ]
            : undefined,
        };

        return NotificationActions.ShowNotification({ notification });
      }),
    ),
  );

  /**
   * Get notification duration based on severity
   */
  private getDuration(severity: 'error' | 'warning'): number {
    // Use environment durations based on severity
    if (severity === 'error') {
      return environment.notification.errorDuration; // 8000ms
    } else {
      // For warnings, use errorDuration if warningDuration is 0
      return environment.notification.warningDuration || environment.notification.errorDuration;
    }
  }
}
